import { useState, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Image, X, Loader2, Upload } from "lucide-react";

export default function ImageUpload({ value, onChange, label = "Add Image", maxSizeMB = 5, className = "" }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || null);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`Image must be less than ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const url = `${API}/files/${res.data.storage_path}`;
      setPreview(url);
      onChange(url);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = () => {
    setPreview(null);
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className={className} data-testid="image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
        data-testid="image-upload-input"
      />

      {preview ? (
        <div className="relative inline-block">
          <img src={preview} alt="Uploaded" className="w-full max-h-48 object-cover rounded-lg border border-slate-200" />
          <button
            type="button"
            onClick={remove}
            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            data-testid="image-upload-remove"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-[#2BAEE5] transition-colors"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 text-[#7C2C3B] animate-spin" />
              <span className="text-sm text-slate-500">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Upload className="w-5 h-5 text-slate-400" />
              </div>
              <span className="text-sm text-slate-500">{label}</span>
              <span className="text-xs text-slate-400">Max {maxSizeMB}MB - JPEG, PNG, WebP</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
